/**
 * PlanetDetails Widget
 * Displays detailed planetary information in a compact tabular format (desktop)
 * or stacked card view (mobile)
 */

import React from 'react';
import { Box, Typography, Table, Chip, Card, Sheet, useTheme } from '@mui/joy';
import { RASHI_NAMES, NAKSHATRA_NAMES } from '../../utils/astroEngine';
import { useMediaQuery } from '@mui/material';

type PlanetName = 'Sun' | 'Moon' | 'Mars' | 'Mercury' | 'Jupiter' | 'Venus' | 'Saturn' | 'Rahu' | 'Ketu';

interface PlanetDetailsProps {
  // Planetary longitudes (sidereal, 0-360)
  grahaLon?: Partial<Record<PlanetName, { lon: number; retro: boolean }>>;
  
  // Planets in houses (1-12)
  placements?: Partial<Record<PlanetName, { house: number; offsetDeg: number }>>;
  
  // Dignity states
  combustMap?: Partial<Record<PlanetName, boolean>>;
  debilMap?: Partial<Record<PlanetName, boolean>>;
  exaltMap?: Partial<Record<PlanetName, boolean>>;
  conjunctDebilMap?: Partial<Record<PlanetName, boolean>>;
  conjunctExaltMap?: Partial<Record<PlanetName, boolean>>;
  
  // Optional: Functional benefic/malefic classification
  functionalMap?: Partial<Record<PlanetName, 'benefic' | 'malefic' | 'neutral'>>;
}

const PLANET_ORDER: PlanetName[] = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu'];

export default function PlanetDetails(props: PlanetDetailsProps) {
  const { grahaLon, placements, combustMap, debilMap, exaltMap, conjunctDebilMap, conjunctExaltMap, functionalMap } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Calculate sign (1-12) from longitude
  const getSign = (lon: number): number => Math.floor(lon / 30) + 1;

  // Calculate nakshatra (1-27) and pada (1-4)
  const getNakshatra = (lon: number): { nakshatra: number; pada: number } => {
    const nakshatraSpan = 360 / 27;
    const nakshatraIndex = Math.floor(lon / nakshatraSpan);
    const nakshatraOffset = lon % nakshatraSpan;
    const pada = Math.floor(nakshatraOffset / (nakshatraSpan / 4)) + 1;
    return { nakshatra: nakshatraIndex + 1, pada };
  };

  // Format longitude in degrees within sign
  const formatLonInSign = (lon: number): string => {
    const degInSign = lon % 30;
    return degInSign.toFixed(2);
  };

  // Get dignity indicators as compact text
  const getDignityIndicators = (planet: PlanetName): React.ReactNode => {
    const indicators: React.ReactElement[] = [];
    
    if (exaltMap?.[planet]) {
      indicators.push(
        <Chip key="exalted" size="sm" color="success" variant="soft" sx={{ fontSize: '0.7rem', py: 0.25, px: 0.75 }}>
          Exalted
        </Chip>
      );
    }
    
    if (conjunctExaltMap?.[planet]) {
      indicators.push(
        <Chip key="conj-exalt" size="sm" color="success" variant="outlined" sx={{ fontSize: '0.7rem', py: 0.25, px: 0.75 }}>
          Conjunct Exalted
        </Chip>
      );
    }
    
    if (debilMap?.[planet]) {
      indicators.push(
        <Chip key="debil" size="sm" color="danger" variant="soft" sx={{ fontSize: '0.7rem', py: 0.25, px: 0.75 }}>
          Debilitated
        </Chip>
      );
    }
    
    if (conjunctDebilMap?.[planet]) {
      indicators.push(
        <Chip key="conj-debil" size="sm" color="danger" variant="outlined" sx={{ fontSize: '0.7rem', py: 0.25, px: 0.75 }}>
          Conjunct Debilitated
        </Chip>
      );
    }
    
    if (combustMap?.[planet]) {
      indicators.push(
        <Chip key="combust" size="sm" color="warning" variant="soft" sx={{ fontSize: '0.7rem', py: 0.25, px: 0.75 }}>
          Combust
        </Chip>
      );
    }
    
    return indicators.length > 0 ? <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>{indicators}</Box> : '—';
  };

  if (!grahaLon) {
    return (
      <Box sx={{ p: 2, borderRadius: 'sm', bgcolor: 'neutral.softBg' }}>
        <Typography level="body-sm" sx={{ fontStyle: 'italic', color: 'neutral.500' }}>
          No planetary data available. Calculate chart first.
        </Typography>
      </Box>
    );
  }

  // Mobile: Stacked card view
  if (isMobile) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {PLANET_ORDER.map((planet) => {
          const lonData = grahaLon[planet];
          if (!lonData) return null;

          const lon = lonData.lon;
          const retro = lonData.retro;
          const sign = getSign(lon);
          const signName = RASHI_NAMES[sign - 1] || `Sign ${sign}`;
          const { nakshatra, pada } = getNakshatra(lon);
          const nakshatraName = NAKSHATRA_NAMES[nakshatra - 1] || `Nak ${nakshatra}`;
          const house = placements?.[planet]?.house;

          return (
            <Card key={planet} variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {/* Planet Name */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography level="title-md" sx={{ fontWeight: 700 }}>
                    {planet}
                    {retro && <Typography component="span" sx={{ color: 'warning.500', fontSize: '0.8rem', ml: 0.5 }}>(R)</Typography>}
                  </Typography>
                  {house && (
                    <Chip size="sm" color="primary" variant="soft">
                      House {house}
                    </Chip>
                  )}
                </Box>
                
                {/* Sign & Position */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary', textTransform: 'uppercase', fontSize: '0.65rem' }}>
                      Sign
                    </Typography>
                    <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                      {signName}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary', textTransform: 'uppercase', fontSize: '0.65rem' }}>
                      Position
                    </Typography>
                    <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                      {formatLonInSign(lon)}°
                    </Typography>
                  </Box>
                  <Box>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary', textTransform: 'uppercase', fontSize: '0.65rem' }}>
                      Nakshatra
                    </Typography>
                    <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                      {nakshatraName} P{pada}
                    </Typography>
                  </Box>
                </Box>
                
                {/* Dignity Status */}
                <Box>
                  <Typography level="body-xs" sx={{ color: 'text.tertiary', textTransform: 'uppercase', fontSize: '0.65rem', mb: 0.5 }}>
                    Dignity
                  </Typography>
                  {getDignityIndicators(planet)}
                </Box>
              </Box>
            </Card>
          );
        })}
      </Box>
    );
  }

  // Desktop: Table view
  return (
    <Box sx={{ 
      fontSize: '0.875rem',
    }}>
      <Table 
        size="sm" 
        sx={{ 
          '& th': { 
            py: 0.5, 
            px: 1,
            fontSize: '0.75rem', 
            fontWeight: 700, 
            textTransform: 'uppercase',
            backgroundColor: 'background.level1',
          },
          '& td': { 
            py: 0.5, 
            px: 1,
            fontSize: '0.875rem',
            lineHeight: 1.4,
          },
          '& td:first-of-type': { fontWeight: 600 },
          '& tr:hover': {
            backgroundColor: 'background.level1',
          },
          '& th:nth-of-type(4), & td:nth-of-type(4)': { 
            width: '50px',
            maxWidth: '50px',
          },
          '& th:nth-of-type(6), & td:nth-of-type(6)': { 
            width: 'auto',
            minWidth: '200px',
          }
        }}
      >
        <thead>
          <tr>
            <th>Planet</th>
            <th>Sign</th>
            <th>Position</th>
            <th>House</th>
            <th>Nakshatra</th>
            <th>Dignity</th>
          </tr>
        </thead>
        <tbody>
          {PLANET_ORDER.map((planet) => {
            const lonData = grahaLon[planet];
            if (!lonData) return null;

            const lon = lonData.lon;
            const retro = lonData.retro;
            const sign = getSign(lon);
            const signName = RASHI_NAMES[sign - 1] || `Sign ${sign}`;
            const { nakshatra, pada } = getNakshatra(lon);
            const nakshatraName = NAKSHATRA_NAMES[nakshatra - 1] || `Nak ${nakshatra}`;
            const house = placements?.[planet]?.house;

            return (
              <tr key={planet}>
                <td>
                  {planet}
                  {retro && <Typography component="span" sx={{ color: 'warning.500', fontSize: '0.7rem', ml: 0.5 }}>(R)</Typography>}
                </td>
                <td>{signName}</td>
                <td>{formatLonInSign(lon)}°</td>
                <td>{house ? `H${house}` : '—'}</td>
                <td>{nakshatraName} P{pada}</td>
                <td>{getDignityIndicators(planet)}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </Box>
  );
}
